import React, { useEffect, useState } from "react";
import Modal from "@components/atoms/Modal";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { Button, CheckBox, Input, VDBText } from "@components/atoms";
import styled, { useTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { ShareExcelModalProps } from "@components/molecules/Share/utils/utils";
import { useTypographyStore } from "@redux/slices/typographySlice";
import Spacer from "@components/atoms/Spacer";
import RadioButton from "@components/atoms/RadioButton";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { ButtonCategory } from "@components/atoms/Button/types";

type ShareMode = "all" | "selected";

interface FormData {
  emailAddress: string;
  subject: string;
  comments: string;
  ccSalesPerson: boolean;
  ccMyself: boolean;
}

const ShareExcelModal = ({
  isOpen,
  onClose,
  enableToastMessage,
  selectedItemsCount = 0,
  onSendEmail,
  fromFavoriteScreen,
}: ShareExcelModalProps & {
  selectedItemsCount: number;
  onSendEmail: (formData: FormData, mode: ShareMode) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();

  const [shareMode, setShareMode] = useState<ShareMode>(
    selectedItemsCount > 0 ? "selected" : "all"
  );
  const [formData, setFormData] = useState<FormData>({
    emailAddress: "",
    subject: "",
    comments: "",
    ccSalesPerson: false,
    ccMyself: false,
  });

  const resetFormData = () => {
    setFormData({
      emailAddress: "",
      subject: "",
      comments: "",
      ccSalesPerson: false,
      ccMyself: false,
    });
  };

  const handleClose = () => {
    resetFormData();
    onClose();
  };

  const handleSend = () => {
    const ccList = [];
    if (formData.ccSalesPerson) ccList.push("salesperson");
    if (formData.ccMyself) ccList.push("myself");

    const emailAddresses = formData?.emailAddress
      .split(/[,;]/)
      .map((email) => email.trim())
      .join(",");

    onSendEmail(
      {
        ...formData,
        emailAddress: emailAddresses,
        ccList,
      },
      shareMode
    );
    setFormData({
      emailAddress: "",
      subject: "",
      comments: "",
      ccSalesPerson: false,
      ccMyself: false,
    });
  };

  const isValidEmail = (email: string) => {
    return email
      ?.split(/[,;]/)
      ?.every((singleEmail) =>
        /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(singleEmail.trim())
      );
  };

  const isValid =
    formData.emailAddress &&
    isValidEmail(formData.emailAddress) &&
    formData.subject;

  useEffect(() => {
    if (selectedItemsCount === 0) {
      setShareMode("all");
    } else if (isOpen) {
      setShareMode("selected");
    }
  }, [selectedItemsCount, isOpen]);

  return (
    <Modal
      headerTitle={`${t("share.emailExcelPDF.title")}${
        selectedItemsCount > 0 ? ` (${selectedItemsCount})` : ""
      }`}
      isOpen={isOpen}
      onClose={handleClose}
      paddingLeft={16}
      paddingRight={16}
      paddingBottom={32}
      paddingTop={16}
      enableToastMessage={enableToastMessage}
      size={375}
    >
      <ContentWrapper>
        <Container>
          {!fromFavoriteScreen && (
            <>
              <RadioButtonContainer>
                <RadioButton
                  label={t("share.emailExcelPDF.allSearchedItems")}
                  labelColor={theme["core-05"]}
                  checked={shareMode === "all"}
                  margin={"0"}
                  onPress={() => setShareMode("all")}
                />
                <Spacer size={SpaceType.Double} />
                <RadioButton
                  label={`${t(
                    "share.emailExcelPDF.selectedItems"
                  )} (${selectedItemsCount})`}
                  checked={shareMode === "selected"}
                  onPress={() => {
                    if (selectedItemsCount > 0) {
                      setShareMode("selected");
                    }
                  }}
                  margin={"0"}
                  disabled={selectedItemsCount === 0}
                  buttonColor={
                    selectedItemsCount === 0
                      ? theme["core-03"]
                      : theme["knob-bor"]
                  }
                />
              </RadioButtonContainer>
              <Spacer size={SpaceType.Big} />
            </>
          )}

          <WarningContainer>
            <VDBIcon
              name={VDBIcons.WARNING}
              variant={IconsVariant.VDBCustom}
              size={20}
              color={theme["core-04"]}
            />
            <VDBText type={typography["Body-R"]}>
              {t("share.emailExcelPDF.noteMaxItems")}
            </VDBText>
          </WarningContainer>
          <Spacer size={SpaceType.Big} />
          <Input
            value={formData.emailAddress}
            onChangeText={(text) =>
              setFormData((prev) => ({ ...prev, emailAddress: text }))
            }
            placeholder={t("share.emailExcelPDF.emailPlaceholder")}
            inputLabel={t("share.emailExcelPDF.emailLabel")}
          />
          <Spacer size={SpaceType.Basic} />
          <VDBText type={typography["Minor-R"]}>
            {t("share.emailExcelPDF.multipleEmails")}
          </VDBText>
          <Spacer size={SpaceType.Big} />
          <Input
            value={formData.subject}
            onChangeText={(text) =>
              setFormData((prev) => ({ ...prev, subject: text }))
            }
            placeholder={t("share.emailExcelPDF.subjectPlaceholder")}
            inputLabel={t("share.emailExcelPDF.subjectLabel")}
          />
          <Spacer size={SpaceType.Big} />
          <Input
            value={formData.comments}
            onChangeText={(text) =>
              setFormData((prev) => ({ ...prev, comments: text }))
            }
            placeholder={t("share.emailExcelPDF.commentsPlaceholder")}
            inputLabel={t("share.emailExcelPDF.commentsLabel")}
            multiline
            numberOfLines={4}
          />
          <Spacer size={SpaceType.Big} />

          {t("share.emailExcelPDF.ccSales") !== "" && (
            <CheckboxContainer>
              <CheckBox
                isChecked={formData.ccSalesPerson}
                onPress={(checked) => {
                  setFormData((prev) => ({ ...prev, ccSalesPerson: checked }));
                }}
                fillColor={theme["knob-bor"]}
                checkBoxSize={20}
              />
              <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
                {t("share.emailExcelPDF.ccSales")}
              </VDBText>
            </CheckboxContainer>
          )}
          <Spacer size={SpaceType.Double} />
          <CheckboxContainer>
            <CheckBox
              isChecked={formData.ccMyself}
              onPress={(checked) => {
                setFormData((prev) => ({ ...prev, ccMyself: checked }));
              }}
              fillColor={theme["knob-bor"]}
              checkBoxSize={20}
            />
            <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
              {t("share.emailExcelPDF.ccMyself")}
            </VDBText>
          </CheckboxContainer>
          <Spacer size={SpaceType.Big} />
          <Button
            title={t("share.emailExcelPDF.sendButton")}
            onPress={handleSend}
            disabled={!isValid}
            category={ButtonCategory.default}
          />
        </Container>
      </ContentWrapper>
    </Modal>
  );
};

const Container = styled(View)`
  padding: ${SpaceType.Basic}px 0;
  ${({ theme }) =>
    theme.isTablet &&
    `
      width: 60%;
      max-width: 600px;
    `}
`;

const ContentWrapper = styled(View)`
  ${({ theme }) =>
    theme.isTablet &&
    `
      align-items: center;
      width: 100%;
    `}
`;

const RadioButtonContainer = styled(View)``;

const CheckboxContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Basic}px;
`;

const WarningContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Basic}px;
  background-color: ${({ theme }) => theme["core-02"]};
  border-radius: ${SpaceType.Basic}px;
  height: ${SpaceType.ExtraLarge}px;
  padding: ${SpaceType.Extra}px;
`;

export default ShareExcelModal;
